// Admin-only paste-and-import page for LLM-produced article JSON.
//
// Use case: the admin copies the article-generation prompt from Prompt
// History, runs it in their own LLM console (ChatGPT, Claude.ai, etc.),
// and pastes the resulting JSON here. Newsdesk parses the JSON and
// drops the article straight into the current property's library —
// no LLM calls, no scraping.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileJson, Loader2, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SAMPLE_KEYS = [
  "headline", "dek", "body_html", "excerpt", "meta_description",
  "facebook_post", "instagram_post", "linkedin_post",
  "wordpress_category_ids", "photo_suggestions", "sources_used",
];

export default function UploadJsonPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [jsonText, setJsonText] = useState("");
  const [writerKey, setWriterKey] = useState("");

  // Pull the property's writers from /settings — same source the article
  // generation UI uses. Attribution requires picking one because the
  // Article model needs a writer_key + writer_name for the byline.
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.getSettings(),
    enabled: user?.role === "admin",
  });

  const writers = settings?.writers || {};
  const writerKeys = Object.keys(writers);

  const uploadMutation = useMutation({
    mutationFn: () => api.uploadJsonArticle(jsonText, writerKey),
    onSuccess: (data) => {
      toast.success("Article imported to the library");
      nav(`/article/${data.article_id}`);
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail || e?.message || "Import failed",
      ),
  });

  // Cheap client-side JSON preview: if the paste parses, count the
  // keys we recognise. Purely informational — the backend does the
  // real validation and returns precise error messages (e.g. line/col
  // of the JSON parse error).
  let parseInfo = null;
  const trimmed = jsonText.trim();
  if (trimmed) {
    try {
      const stripped = trimmed
        .replace(/^```(?:json)?/i, "")
        .replace(/```$/, "")
        .trim();
      const parsed = JSON.parse(stripped);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const matched = SAMPLE_KEYS.filter((k) => k in parsed);
        const missing = ["headline", "body_html"].filter((k) => !(k in parsed));
        parseInfo = {
          ok: missing.length === 0,
          matched,
          missing,
          headline: parsed.headline,
          bodyChars: (parsed.body_html || "").length,
        };
      } else {
        parseInfo = { ok: false, error: "JSON must be an object" };
      }
    } catch (e) {
      parseInfo = { ok: false, error: e.message };
    }
  }

  if (user?.role !== "admin") {
    return (
      <div className="p-10">
        <div className="font-serif-display text-2xl">Not authorized</div>
        <div className="text-sm text-[var(--ink-muted)] mt-2">
          Only admins can access this page.
        </div>
      </div>
    );
  }

  const canSubmit =
    !!jsonText.trim() &&
    !!writerKey &&
    !uploadMutation.isPending &&
    (parseInfo?.ok !== false);

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-[var(--border)] bg-white sticky top-0 z-10">
        <div className="px-4 sm:px-10 py-5 sm:py-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="overline mb-1">Admin · Upload JSON</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl leading-tight">
              Paste article JSON
            </h1>
            <p className="text-sm text-[var(--ink-muted)] mt-2 max-w-2xl">
              Ran the article-generation prompt in your own LLM console?
              Paste the JSON result here and Newsdesk will drop it straight
              into the current property&apos;s library — no scraping, no
              LLM billing.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-10 py-6 sm:py-8">
        <div className="max-w-3xl space-y-5">
          <div>
            <label
              className="block text-xs uppercase tracking-wider text-[var(--ink-muted)] mb-1.5 font-mono-display"
              htmlFor="upload-json-writer"
            >
              Byline writer
            </label>
            <select
              id="upload-json-writer"
              data-testid="upload-json-writer-select"
              value={writerKey}
              onChange={(e) => setWriterKey(e.target.value)}
              className="w-full max-w-sm px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
            >
              <option value="">Select a writer…</option>
              {writerKeys.map((k) => (
                <option key={k} value={k}>
                  {writers[k].name || k}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-[var(--ink-muted)] mt-1.5">
              Required — this becomes the article byline. The JSON itself
              doesn&apos;t carry writer info because prompts run outside
              Newsdesk don&apos;t know which voice you used.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label
                className="block text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display"
                htmlFor="upload-json-textarea"
              >
                Article JSON
              </label>
              {parseInfo && (
                <span
                  data-testid="upload-json-parse-status"
                  className={
                    "text-[11px] font-mono-display px-1.5 py-0.5 border " +
                    (parseInfo.ok
                      ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                      : "bg-red-50 text-red-700 border-red-300")
                  }
                >
                  {parseInfo.ok
                    ? `Valid · ${parseInfo.matched.length} fields recognised`
                    : parseInfo.error ||
                      `Missing: ${parseInfo.missing?.join(", ")}`}
                </span>
              )}
            </div>
            <textarea
              id="upload-json-textarea"
              data-testid="upload-json-textarea"
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              rows={22}
              spellCheck={false}
              placeholder={
                '{\n  "headline": "…",\n  "dek": "…",\n  "body_html": "<p>…</p>",\n  "excerpt": "…",\n  "meta_description": "…",\n  "facebook_post": "…",\n  "instagram_post": "…",\n  "linkedin_post": "…",\n  "wordpress_category_ids": [123, 456],\n  "photo_suggestions": [{"concept": "…", "adobe_stock_url": "…", "envato_url": "…", "unsplash_url": "…"}],\n  "sources_used": ["https://…"]\n}'
              }
              className="w-full resize-y px-3 py-2 text-xs font-mono-display bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)]"
            />
            {parseInfo?.ok && (
              <div className="mt-2 text-[11px] text-[var(--ink-muted)] font-mono-display">
                <span className="text-[var(--ink)]">
                  {parseInfo.headline || "(no headline)"}
                </span>{" "}
                · body {parseInfo.bodyChars.toLocaleString()} chars
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              data-testid="upload-json-submit"
              onClick={() => uploadMutation.mutate()}
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[var(--slate-900)] text-white hover:opacity-90 disabled:opacity-40"
            >
              {uploadMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Import to library
            </button>
            <button
              type="button"
              data-testid="upload-json-clear"
              onClick={() => setJsonText("")}
              disabled={!jsonText || uploadMutation.isPending}
              className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)] disabled:opacity-40"
            >
              Clear
            </button>
          </div>

          <div className="mt-6 text-[11px] text-[var(--ink-muted)] leading-relaxed border-t border-[var(--border)] pt-4">
            <div className="flex items-center gap-1.5 mb-1">
              <FileJson className="w-3 h-3" />
              <span className="uppercase tracking-wider font-mono-display">
                Recognised fields
              </span>
            </div>
            <p>
              Newsdesk reads:{" "}
              <span className="font-mono-display">
                headline, dek, body_html, excerpt, meta_description,
                facebook_post, instagram_post, linkedin_post,
                wordpress_category_ids, photo_suggestions, sources_used
              </span>
              . Anything else in the JSON is ignored. Only{" "}
              <span className="font-mono-display">headline</span> and{" "}
              <span className="font-mono-display">body_html</span> are
              required — the rest fall back to blank. Fenced code blocks
              (<span className="font-mono-display">```json … ```</span>)
              are stripped automatically before parsing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
